import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
    Menu,
    X,
    ChevronDown,
    CheckCircle,
    Fingerprint,
    Info,
    Mail,
    ShieldCheck,
    FileText,
    HelpCircle,
    Sparkle,
    Home,
    Briefcase,
    ShoppingBag,
    Calculator,
    Truck,
    LogOut,
    ArrowRight,
    BookOpen,
    Phone,
    MapPin,
    MessageCircle,
    User,
    Settings,
    LayoutDashboard,
    ShoppingCart
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useCart } from "@/context/CartContext";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { supabase } from "@/integrations/supabase/client";
import { api } from "@/lib/api";
import { NotificationBell } from "@/components/notifications/NotificationBell";

const ListItem: React.FC<{
    to: string;
    title: string;
    children: React.ReactNode;
}> = ({ to, title, children }) => (
    <li>
        <Link
            to={to}
            className="flex items-start p-3 rounded-xl hover:bg-slate-50 transition-all duration-300 group relative overflow-hidden"
        >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-50 text-blue-600 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-all duration-300 shadow-sm group-hover:shadow-md group-hover:scale-110">
                {children}
            </div>
            <div className="ml-4 relative">
                <p className="text-sm font-semibold text-slate-700 group-hover:text-blue-700 transition-colors duration-300">{title}</p>
            </div>
        </Link>
    </li>
);

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
    const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
    const [isMobileUserMenuOpen, setIsMobileUserMenuOpen] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

    const { user, signOut } = useAuth();
    const { cart } = useCart();
    const location = useLocation();
    const subMenuRef = useRef<HTMLDivElement>(null);
    const moreMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        if (!user?.id) return;
        const fetchAvatar = async () => {
            try {
                // First try direct user metadata
                if (user.user_metadata?.avatar_url || user.user_metadata?.picture) {
                    setAvatarUrl(user.user_metadata.avatar_url || user.user_metadata.picture);
                }
                
                // Then fetch profile via API to see if explicit avatar_url is set
                const profiles = await api.get(`/data/profiles?id=${user.id}`);
                const data = Array.isArray(profiles) ? profiles[0] : null;

                if (data?.avatar_url) {
                    setAvatarUrl(data.avatar_url);
                }
            } catch (err) {
                console.error("Error fetching avatar in header", err);
            }
        };
        fetchAvatar();
    }, [user?.id]);

    useEffect(() => {
        setIsMenuOpen(false);
        setIsSubMenuOpen(false);
        setIsMoreMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (subMenuRef.current && !subMenuRef.current.contains(event.target as Node)) {
                setIsSubMenuOpen(false);
            }
            if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
                setIsMoreMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => { document.body.style.overflow = "unset"; };
    }, [isMenuOpen]);

    const navLinks = [
        { name: "Home", path: "/", icon: <Home className="h-5 w-5" /> },
        { name: "Services", path: "/services", icon: <Briefcase className="h-5 w-5" /> },
        { name: "Tax Return", path: "/tax-return-info", icon: <FileText className="h-5 w-5" /> },
        { name: "Shop", path: "/shop", icon: <ShoppingBag className="h-5 w-5" /> },
        { name: "Tax Calculator", path: "/tax-calculator", icon: <Calculator className="h-5 w-5" /> },
        { name: "Track", path: "/track-order", icon: <Truck className="h-5 w-5" /> },
    ];

    const moreLinks = [
        { to: "/about", title: "About", icon: <Info className="h-6 w-6" /> },
        { to: "/blog", title: "Blog", icon: <BookOpen className="h-6 w-6" /> },
        { to: "/contact", title: "Contact", icon: <Mail className="h-6 w-6" /> },
        { to: "/privacy", title: "Privacy", icon: <ShieldCheck className="h-6 w-6" /> },
        { to: "/terms", title: "Terms", icon: <FileText className="h-6 w-6" /> },
        { to: "/reviews", title: "Reviews", icon: <Sparkle className="h-6 w-6" /> },
        { to: "/faqs", title: "FAQs", icon: <HelpCircle className="h-6 w-6" /> },
    ];

    const handleMobileMenuClose = () => {
        setIsMenuOpen(false);
    };

    const handleLogout = async () => {
        await signOut();
    };

    return (
        <>
            <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .shimmer-text {
          background: linear-gradient(90deg, #0f172a 0%, #3b82f6 50%, #0f172a 100%);
          background-size: 200% auto;
          color: transparent;
          -webkit-background-clip: text;
          background-clip: text;
          animation: shimmer 3s linear infinite;
        }
        .nav-link-underline {
            position: relative;
        }
        .nav-link-underline::after {
            content: '';
            position: absolute;
            width: 0;
            height: 2px;
            bottom: -4px;
            left: 0;
            background-color: #2563eb;
            transition: width 0.3s ease;
            border-radius: 2px;
        }
        .nav-link-underline:hover::after {
            width: 100%;
        }
      `}</style>
            <header
                className="fixed top-0 left-0 right-0 z-40 transition-all duration-300 bg-white/95 backdrop-blur-md shadow-sm py-2"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        {/* Logo Area */}
                        <Link to="/" className="flex items-center group relative z-10 w-fit">
                            <img src="/logo_hd.png" width="160" height="60" fetchPriority="high" alt="Make Me Filer Logo" className="h-[2.75rem] sm:h-[3.25rem] w-auto object-contain transition-transform group-hover:scale-105" />
                        </Link>

                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex items-center space-x-6">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    className="text-base font-medium text-slate-600 hover:text-blue-600 transition-colors nav-link-underline"
                                >
                                    {link.name}
                                </Link>
                            ))}

                            {/* Verify Dropdown */}
                            <div className="relative" ref={subMenuRef}>
                                <button
                                    onClick={() => setIsSubMenuOpen(!isSubMenuOpen)}
                                    className="flex items-center text-base font-medium text-slate-600 hover:text-blue-600 transition-colors nav-link-underline group"
                                >
                                    Verify
                                    <ChevronDown
                                        className={`h-4 w-4 ml-1 transition-transform duration-300 ${isSubMenuOpen ? "rotate-180 text-blue-600" : "text-slate-400 group-hover:text-blue-600"}`}
                                    />
                                </button>
                                {isSubMenuOpen && (
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-72 origin-top transition-all duration-300 z-50">
                                        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-2 animate-in fade-in zoom-in-95 duration-200">
                                            <ul>
                                                <ListItem to="/ntn-verification" title="NTN Verification">
                                                    <Fingerprint className="h-6 w-6" />
                                                </ListItem>
                                                <ListItem to="/atl-verification" title="ATL Verification">
                                                    <CheckCircle className="h-6 w-6" />
                                                </ListItem>
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* More Dropdown */}
                            <div className="relative" ref={moreMenuRef}>
                                <button
                                    onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                                    className="flex items-center text-base font-medium text-slate-600 hover:text-blue-600 transition-colors nav-link-underline group"
                                >
                                    More
                                    <ChevronDown
                                        className={`h-4 w-4 ml-1 transition-transform duration-300 ${isMoreMenuOpen ? "rotate-180 text-blue-600" : "text-slate-400 group-hover:text-blue-600"}`}
                                    />
                                </button>
                                {isMoreMenuOpen && (
                                    <div className="absolute top-full right-0 mt-4 w-72 origin-top-right transition-all duration-300 z-50">
                                        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-2 animate-in fade-in zoom-in-95 duration-200">
                                            <ul>
                                                {moreLinks.map((link) => (
                                                    <ListItem key={link.to} to={link.to} title={link.title}>
                                                        {link.icon}
                                                    </ListItem>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* AI Button */}
                            <Link
                                to="/ai"
                                className="flex items-center px-4 py-2 text-base font-bold text-slate-700 bg-slate-100 rounded-full hover:bg-blue-50 hover:text-blue-600 transition-all group"
                            >
                                AI <Sparkle className="h-4 w-4 ml-1.5 text-yellow-500 fill-yellow-400 animate-pulse" />
                            </Link>
                        </nav>

                        {/* Actions */}
                        <div className="flex items-center space-x-3 lg:space-x-5">
                            {user && (
                                <NotificationBell />
                            )}

                            {/* Shopping Cart Icon */}
                            <Link to="/checkout" aria-label="Go to checkout" className="relative p-2 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 hover:text-indigo-700 transition-all duration-300 rounded-full flex items-center justify-center shadow-sm">
                                <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6" />
                                {cart.length > 0 && (
                                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-gradient-to-r from-red-500 to-rose-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full shadow-md animate-in zoom-in">
                                        {cart.length}
                                    </span>
                                )}
                            </Link>

                            {user ? (
                                <>
                                    {/* Desktop User Actions */}
                                    <div className="hidden sm:flex items-center space-x-2 lg:space-x-4">
                                        <Button asChild className="rounded-full px-4 lg:px-6 bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all hover:scale-105 active:scale-95 text-sm lg:text-base">
                                            <Link to="/dashboard">Dashboard</Link>
                                        </Button>
                                        <Link to="/dashboard?tab=profile" className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors border border-slate-200 shadow-sm overflow-hidden" title="Profile">
                                            {avatarUrl ? (
                                                <img src={avatarUrl} loading="lazy" alt="User Avatar" className="h-full w-full object-cover" />
                                            ) : user.user_metadata?.full_name ? (
                                                <span className="font-bold text-slate-700">{user.user_metadata.full_name.charAt(0).toUpperCase()}</span>
                                            ) : (
                                                <User className="h-5 w-5 text-slate-600" />
                                            )}
                                        </Link>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={handleLogout}
                                            className="rounded-full text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                                            title="Logout"
                                        >
                                            <LogOut className="h-5 w-5" />
                                        </Button>
                                    </div>
                                    
                                    {/* Mobile User Avatar Dropdown */}
                                    <div className="sm:hidden relative">
                                        <button 
                                            onClick={() => setIsMobileUserMenuOpen(!isMobileUserMenuOpen)}
                                            className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200 transition-colors border border-slate-200 shadow-sm overflow-hidden"
                                        >
                                            {/* We check for avatar, or fallback to user metadata full name initial, or default to User icon */}
                                            {avatarUrl ? (
                                                <img src={avatarUrl} loading="lazy" alt="User Avatar" className="h-full w-full object-cover" />
                                            ) : user.user_metadata?.full_name ? (
                                                <span className="font-bold text-slate-700">{user.user_metadata.full_name.charAt(0).toUpperCase()}</span>
                                            ) : (
                                                <User className="h-5 w-5" />
                                            )}
                                        </button>
                                        
                                        {isMobileUserMenuOpen && (
                                            <>
                                                <div className="fixed inset-0 z-40" onClick={() => setIsMobileUserMenuOpen(false)}></div>
                                                <div className="absolute top-full right-0 mt-3 w-56 bg-white rounded-2xl shadow-xl border border-slate-100 p-2 animate-in fade-in zoom-in-95 duration-200 z-50">
                                                    <div className="px-3 py-2 border-b border-slate-50 mb-2">
                                                        <p className="text-sm font-semibold text-slate-900 truncate">{user.user_metadata?.full_name || 'My Account'}</p>
                                                        <p className="text-xs text-slate-500 truncate">{user.email}</p>
                                                    </div>
                                                    <Link to="/dashboard" onClick={() => setIsMobileUserMenuOpen(false)} className="flex items-center w-full px-3 py-2 text-sm text-slate-600 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-colors">
                                                        <LayoutDashboard className="h-4 w-4 mr-2" />
                                                        Dashboard
                                                    </Link>
                                                    <Link to="/dashboard?tab=profile" onClick={() => setIsMobileUserMenuOpen(false)} className="flex items-center w-full px-3 py-2 mt-1 text-sm text-slate-600 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-colors">
                                                        <Settings className="h-4 w-4 mr-2" />
                                                        Profile Settings
                                                    </Link>
                                                    <button onClick={() => { setIsMobileUserMenuOpen(false); handleLogout(); }} className="flex items-center w-full px-3 py-2 mt-1 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-colors">
                                                        <LogOut className="h-4 w-4 mr-2" />
                                                        Logout
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-center bg-slate-900 rounded-full p-1 shadow-lg shadow-slate-900/20">
                                    <Link
                                        to="/login"
                                        className="px-3 py-1.5 lg:px-4 lg:py-2 text-xs lg:text-sm font-semibold text-white hover:text-blue-200 transition-colors"
                                    >
                                        Log In
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Menu Overlay */}
            {isMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div
                        className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm transition-opacity"
                        onClick={handleMobileMenuClose}
                    />
                    <div
                        className={`fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl transform transition-transform duration-300 ease-out flex flex-col ${isMenuOpen ? "translate-x-0" : "translate-x-full"
                            }`}
                    >
                        <div className="flex justify-between items-center p-6 border-b border-slate-100">
                            <span className="text-xl font-bold text-slate-900">Menu</span>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleMobileMenuClose}
                                className="hover:bg-slate-100 rounded-full"
                                aria-label="Close menu"
                            >
                                <X className="h-6 w-6 text-slate-500" />
                            </Button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-2">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    className="flex items-center px-4 py-3 text-lg font-medium text-slate-700 rounded-xl hover:bg-slate-50 transition-colors"
                                    onClick={handleMobileMenuClose}
                                >
                                    <span className="mr-3 text-slate-400">{link.icon}</span>
                                    {link.name}
                                </Link>
                            ))}
                            <div className="border-t border-slate-100 my-4 pt-4">
                                <p className="px-4 text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Verify</p>
                                <Link to="/ntn-verification" className="flex items-center px-4 py-3 text-lg font-medium text-slate-700 rounded-xl hover:bg-slate-50" onClick={handleMobileMenuClose}>
                                    <Fingerprint className="mr-3 h-5 w-5 text-slate-400" /> NTN Verification
                                </Link>
                                <Link to="/atl-verification" className="flex items-center px-4 py-3 text-lg font-medium text-slate-700 rounded-xl hover:bg-slate-50" onClick={handleMobileMenuClose}>
                                    <CheckCircle className="mr-3 h-5 w-5 text-slate-400" /> ATL Verification
                                </Link>
                            </div>

                            <div className="border-t border-slate-100 my-4 pt-4">
                                <Link to="/ai" className="flex items-center px-4 py-3 text-lg font-bold text-blue-600 rounded-xl hover:bg-blue-50" onClick={handleMobileMenuClose}>
                                    <Sparkle className="mr-3 h-5 w-5" /> AI Assistant
                                </Link>
                            </div>

                            <div className="mt-auto pt-6">
                                {user ? (
                                    <div className="space-y-3">
                                        <Button className="w-full h-12 text-lg font-semibold rounded-xl bg-blue-600 hover:bg-blue-700" asChild>
                                            <Link to="/dashboard" onClick={handleMobileMenuClose}>Dashboard</Link>
                                        </Button>
                                        <Button variant="outline" className="w-full h-12 text-lg font-semibold rounded-xl text-red-600 border-red-100 hover:bg-red-50" onClick={() => { handleLogout(); handleMobileMenuClose(); }}>
                                            Logout
                                        </Button>
                                    </div>
                                ) : (
                                    <Button className="w-full h-12 text-lg font-semibold rounded-xl bg-slate-900 hover:bg-slate-800" asChild>
                                        <Link to="/login" onClick={handleMobileMenuClose}>Get Started</Link>
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;
